define((require) => {
    "use strict";

    const BreakpointRange = require("shared/ui/breakpoint/BreakpointRange");
    const Breakpoint      = require("shared/ui/breakpoint/Breakpoint");

    return {

        /**
         * Returns all valid entries in ascending order by threshold for all valid breakpoint entries,
         * where an asset (experience) with multiple successive breakpoints will be collapsed into a single entry
         * describing the maximum threshold. <p>
         *
         * The return array can thus still contain duplicate assets, but not with successive breakpoints. <p>
         *
         * The viewer will not really need to filter our invalid entries as we only push valid group entries,
         * but this is also used for DnD where the group may contain invalid entries.
         *
         * @return {[asset, value][]} A 2D array containing the <tt>[asset, value]</tt> pairs for each
         *                                (collapsed) entry; never falsey, but can be empty.
         *
         * @see #entries
         */
        sortedEntries() {
            const sorted = [];
            let entries = this.entries();
            let entry;
            let lastEntry;
            let asset;
            let breakpoint;
            let i;

            // Filter out all invalid entries, i.e. those without both an asset reference and an actual breakpoint
            // value, before we sort. This is not an issue for the viewer, since it uses pushed data, but required
            // for the channel admin application to be able to present the correct information to the user doing
            // the DnD Publishing...
            for (i = 0; i < entries.length; i++) {
                entry = entries[i];
                breakpoint = entry[1];
                if (breakpoint && breakpoint.has("threshold")) {
                    asset = entry[0];
                    if (asset) {
                        sorted.push(entry);
                    }
                }
            }

            // Sort according to ascending threshold...
            sorted.sort((a, b) => a[1].get("threshold") - b[1].get("threshold"));

            // Clear entries to be populated with collapsed entries based on sorted order...
            entries = [];
            for (i = 0; i < sorted.length; i++) {
                entry = sorted[i];
                // We know that ids are globally unique across asset types, so it suffices to check on
                // resource id here. If the ids match, we simply skip the current entry as an entry
                // for the same asset is already in "entries" with the lowest possible threshold...
                if (!lastEntry || (lastEntry[0].get("id") !== entry[0].get("id"))) {
                    entries.push(entry);
                }

                // Save for next iteration...
                lastEntry = entry;
            }

            return entries;
        },

        /**
         * @typedef {function(BreakpointRange):*} BreakpointRangeCallback function that takes a BreakpointRange as a parameter. Can return anything.
         */

        /**
         * Invokes the specified callback of the format <tt>fn(range:BreakpointRange, asset:Reference, group:BreakpointGroup)</tt>
         * for each range deduced from this group, in <b>ascending</b> range order without any gaps. <p>
         *
         * The first range iterated over here will always <b>end</b> with <tt>Breakpoint.INFINITE</tt>, and the last will always
         * <b>start</b> with <tt>Breakpoint.BEGIN</tt>. In case only a single range is contained, this will also be true. In
         * case this group is empty, <tt>callback</tt> will not be invoked.
         *
         * @param {BreakpointRangeCallback} callback The callback to invoke for each range deduced from this group; cannot be falsey.
         *                            If a value is returned, the iteration will stop and said value returned.
         *
         * @return {*} The first value returned by callback, if any; can be undefined.
         */
        forEachRange(callback) { // todo this doesn't go through the whole range. Rename to something like `firstMatchInRange`?
            const entries = this.sortedEntries();
            const n = entries.length - 1;
            let from = Breakpoint.BEGIN;
            let result;
            let to;
            let entry;
            let i;

            // First range always start with Breakpoint.BEGIN, last range always end with Breakpoint.INFINITY; they
            // can be the same single range...
            for (i = 0; i <= n; i++) {
                entry = entries[i];
                to = i === n ? Breakpoint.INFINITY : entries[i + 1][1];
                result = callback(new BreakpointRange(from, to), entry[0], this);
                if (typeof result !== "undefined") {
                    return result;
                }
                from = to;
            }
            // undefined
        },

        /**
         * Returns a ranges deduced from this group, in range order, without associated assets.
         *
         * @return {BreakpointRange[]} The ranges deduced from this group, in range order; never falsey, but can
         *                             be empty.
         *
         * @see    #rangeAssets
         * @see    #rangeMap
         */
        ranges() {
            const ranges = [];
            this.forEachRange((range) => {
                ranges.push(range);
            });
            return ranges;
        },

        /**
         * Returns a unique assets deduced from the ranges of this group, in range order, but with no duplicates.
         *
         * @return {[]} The assets deduced from the ranges of this group; never falsey, but can be empty.
         *
         * @see    #ranges
         * @see    #rangeMap
         */
        rangeAssets() {
            const assets = [];
            // Range is unique, but assets may not be, i.e. R1:E1, R2:E2, R3:E1 again. This is O(n2), but we assume
            // very few entries, i.e <= 5 on average case...
            this.forEachRange((range, asset) => {
                let i;
                for (i = 0; i < assets.length; i++) {
                    if (assets[i].get("id") === asset.get("id")) {
                        return;
                    }
                }
                assets.push(asset);
            });
            return assets;
        },

        /**
         * Returns a map indexed by the ranges deduced from this group where the associated values are the
         * asset associated with said range. <p>
         *
         * The same asset may be stored numerous times as a value in the returned map.
         *
         * @return {Map.<BreakpointRange,{resourceId:String, resourceType:String}} The map of ranges indexing their
         *         associated asset (experience) via a reference; never falsey, but can be empty.
         *
         * @see    #ranges
         */
        rangeMap() {
            const map = new Map();
            this.forEachRange((range, asset) => {
                map.set(range, asset);
            });
            return map;
        },

        /**
         * Returns a user friendly description of this group (primarily for logging).
         *
         * @param  {Boolean} [excludeIds] True to exclude asset ids in the description, falsey to include them (default).
         * @return {String} A user friendly description; never null.
         */
        describe(excludeIds) {
            let s = "";
            this.forEachRange((range, asset) => {
                if (s) {
                    s += ", ";
                }
                s += range;
                if (!excludeIds) {
                    s += `'${asset.get("id")}'`;
                }
            });
            return s;
        },

        /**
         * Returns the (to) breakpoint from this group matching the specified threshold.
         *
         * @param  {Number} threshold The current threshold/width.
         * @return {{breakpoint: Breakpoint, reference:Reference}} The matching range and associated asset; can only be
         *                                                         falsey if this group is empty.
         */
        fromThreshold(threshold) {
            threshold = Math.max(0, threshold);

            return this.forEachRange((range, asset) => {
                if (range.contains(threshold)) {
                    return {
                        breakpoint: range.breakpoint(),
                        asset
                    };
                }
            });
        }
    };
});



// WEBPACK FOOTER //
// ./src/shared/ui/breakpoint/BreakpointGroupRangeMixinPrototype.js